import { PropTypes } from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { Drawer } from "@/atoms";
import { BulkDistributionResults, ReleaseDistributionForm, ReleaseVideoDistributionForm } from "@/components";
import { actionsData, selectionsData } from "@/data";
import { useCreateBulkDistributions, useCreateDistribution } from "@/hooks";
import { toggleDistributionDrawer } from "@/redux/actions";

const DistributionDrawer = ({ hasBulkDistributions, hasRefetchDistributions, ids, limit, page }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const params = useParams();

    const { distributionTypes: distributionTypesSelectionData } = selectionsData.data;

    const [releaseDistributionTypeOption, videoDistributionTypeOption] = distributionTypesSelectionData;

    const [distributionType, setDistributionType] = useState(releaseDistributionTypeOption.value);

    // set only when a bulk distribution could not queue everything, and swaps the form for what failed and why
    const [bulkResults, setBulkResults] = useState(null);

    const open = useSelector((state) => state.distributionsReducer?.openDrawer);

    const releaseId = params?.id && parseInt(params?.id);

    const releasesIds = hasBulkDistributions ? ids : [releaseId];

    const { createDistribution, loading: createDistributionLoading } = useCreateDistribution(
        releaseId,
        hasRefetchDistributions,
        limit,
        page,
    );

    const { createBulkDistributions, loading: createBulkDistributionsLoading } = useCreateBulkDistributions(
        ids,
        setBulkResults,
    );

    const isVideoDistribution = distributionType === videoDistributionTypeOption.value;

    const closeDrawerHandler = () => dispatch(toggleDistributionDrawer(false));

    const [previousOpen, setPreviousOpen] = useState(open);

    // reopening the drawer always starts on the default type's form rather than the last type picked or the last bulk
    // results. the reset runs on open (during render) and not on close, otherwise the release form would be swapped in
    // while the drawer slides out, firing and then cancelling its clients, territories and tracks queries
    if (open !== previousOpen) {
        setPreviousOpen(open);

        if (open) {
            setDistributionType(releaseDistributionTypeOption.value);

            setBulkResults(null);
        }
    }

    return (
        <Drawer
            closeAction={closeDrawerHandler}
            open={open}
            title={
                bulkResults
                    ? t("releases.bulkDistributionJobsSummary")
                    : t(`releases.${hasBulkDistributions ? "distributeReleases" : "distributeRelease"}`)
            }
        >
            {bulkResults ? (
                <BulkDistributionResults results={bulkResults} />
            ) : isVideoDistribution ? (
                <ReleaseVideoDistributionForm
                    action={actionsData.distribute}
                    cancelAction={closeDrawerHandler}
                    distributionType={distributionType}
                    hasBulkDistributions={hasBulkDistributions}
                    hasRefetchDistributions={hasRefetchDistributions}
                    limit={limit}
                    page={page}
                    releasesIds={releasesIds}
                    setBulkResults={setBulkResults}
                    setDistributionType={setDistributionType}
                />
            ) : (
                <ReleaseDistributionForm
                    action={actionsData.distribute}
                    cancelAction={closeDrawerHandler}
                    distributionType={distributionType}
                    loading={createDistributionLoading || createBulkDistributionsLoading}
                    setDistributionType={setDistributionType}
                    onSubmitForm={hasBulkDistributions ? createBulkDistributions : createDistribution}
                />
            )}
        </Drawer>
    );
};

export default DistributionDrawer;

DistributionDrawer.propTypes = {
    hasBulkDistributions: PropTypes.bool,
    hasRefetchDistributions: PropTypes.bool,
    ids: PropTypes.array,
    limit: PropTypes.number,
    page: PropTypes.number,
};
